import { createTheme, alpha } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { pageTheme } from './pageTheme';

export const darkTheme = createTheme({
  ...pageTheme,
  palette: {
    ...pageTheme.palette,
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#21201d'
    },
    text: {
      primary: pageTheme.colors.alpha.white[100],
      secondary: pageTheme.colors.alpha.trueWhite[70],
      disabled: pageTheme.colors.alpha.trueWhite[50]
    },
    // pageTheme's action colours are black-based (built for light surfaces);
    // without this, disabled controls render dark-on-dark (e.g. the disabled
    // Previous button was black text on the dark nav bar).
    action: {
      ...pageTheme.palette.action,
      // default icon/control colour — black-based in pageTheme, so an
      // uncoloured icon (e.g. the mobile back arrow) rendered black on dark.
      active: pageTheme.colors.alpha.trueWhite[70],
      disabled: pageTheme.colors.alpha.trueWhite[50],
      disabledBackground: alpha(pageTheme.colors.alpha.trueWhite[100], 0.12)
    }
  },
  sidebar: {
    ...pageTheme.sidebar,
    background: '#21201d',
    textColor: pageTheme.colors.alpha.trueWhite[70],
    dividerBg: alpha(pageTheme.colors.alpha.trueWhite[100], 0.1),
    menuItemColor: pageTheme.colors.alpha.trueWhite[70],
    menuItemColorActive: pageTheme.colors.alpha.white[100],
    menuItemBg: '#21201d',
    menuItemBgActive: '#2a2d3e',
    menuItemIconColor: pageTheme.colors.alpha.trueWhite[50],
    menuItemIconColorActive: pageTheme.colors.alpha.white[100],
    menuItemHeadingColor: pageTheme.colors.alpha.trueWhite[100]
  },
  header: {
    ...pageTheme.header,
    background: '#21201d',
    textColor: pageTheme.colors.alpha.white[100]
  },
  typography: {
    ...pageTheme.typography,
    h3: {
      ...pageTheme.typography.h3,
      color: pageTheme.colors.alpha.white[100]
    },
    subtitle1: {
      ...pageTheme.typography.subtitle1,
      color: pageTheme.colors.alpha.trueWhite[70]
    },
    subtitle2: {
      ...pageTheme.typography.subtitle2,
      color: pageTheme.colors.alpha.white[100]
    },
    caption: {
      ...pageTheme.typography.caption,
      color: pageTheme.colors.alpha.trueWhite[50]
    }
  },
  // Deep-merge the dark-specific tweaks ONTO pageTheme's component overrides.
  // A plain object would replace `components` wholesale and dark mode would
  // lose every custom component style (buttons, paper shadows, code blocks,
  // tooltips, stepper, switches, the CssBaseline globals, etc.).
  //
  // Because pageTheme hardcodes black text colours that assume a light
  // surface, the merge below also re-colours the components that would
  // otherwise paint dark text on the dark paper/menu surfaces. Selector
  // strings mirror pageTheme exactly so they overlay instead of duplicating.
  components: deepmerge(pageTheme.components, {
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: pageTheme.colors.alpha.trueWhite[70]
        }
      }
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          background: pageTheme.colors.alpha.black[30],
          '&:hover, &.Mui-selected, &.Mui-selected:hover': {
            color: pageTheme.colors.alpha.black[100],
            background: pageTheme.palette.primary.main
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: pageTheme.colors.alpha.white[10]
          }
        }
      }
    },
    // Inline code (lesson settings paths) — dark navy text on dark would
    // vanish; give it a light-on-translucent chip instead.
    MuiCssBaseline: {
      styleOverrides: {
        code: {
          background: alpha(pageTheme.colors.alpha.trueWhite[100], 0.12),
          color: pageTheme.colors.alpha.trueWhite[100]
        }
      }
    },
    // Text/outlined buttons need light text on dark; contained variants keep
    // their dark-on-colour text (untouched). pageTheme gives every button a
    // faint-yellow base background (fine on white, broken on dark paper), so
    // reset the base to transparent — contained variants set their own colour.
    MuiButton: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent'
        },
        text: {
          color: pageTheme.colors.alpha.trueWhite[100]
        },
        outlinedPrimary: {
          color: pageTheme.colors.alpha.trueWhite[100]
        },
        outlinedSecondary: {
          backgroundColor: 'transparent',
          color: pageTheme.colors.alpha.trueWhite[100],
          '&:hover, &.MuiSelected': {
            backgroundColor: alpha(pageTheme.colors.alpha.trueWhite[100], 0.06),
            color: pageTheme.colors.alpha.trueWhite[100]
          }
        }
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        colorPrimary: {
          background: alpha(pageTheme.colors.alpha.trueWhite[100], 0.05),
          color: pageTheme.colors.alpha.trueWhite[70]
        }
      }
    },
    // Only the resting item colour (on the dark menu surface) needs lightening;
    // the hover/selected states keep pageTheme's dark text because their
    // highlight background is a light yellow in both themes.
    MuiMenu: {
      styleOverrides: {
        list: {
          '& .MuiMenuItem-root.MuiButtonBase-root': {
            color: pageTheme.colors.alpha.trueWhite[70]
          }
        }
      }
    },
    // The dropdown caret defaults to a dark colour (black-on-dark in dark mode).
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: pageTheme.colors.alpha.trueWhite[70]
        },
        iconOutlined: {
          color: pageTheme.colors.alpha.trueWhite[70]
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.MuiButtonBase-root': {
            color: pageTheme.colors.alpha.trueWhite[70]
          }
        }
      }
    },
    // Only the unselected-hover state needs lightening (dark text on dark with
    // no highlight bg). The selected tab keeps dark text — its indicator is a
    // light-yellow pill in both themes.
    MuiTab: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: pageTheme.colors.alpha.trueWhite[100]
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        colorSecondary: {
          background: alpha(pageTheme.colors.alpha.trueWhite[100], 0.1),
          color: pageTheme.colors.alpha.trueWhite[100],
          '&:hover': {
            background: alpha(pageTheme.colors.alpha.trueWhite[100], 0.16)
          }
        }
      }
    },
    MuiAvatarGroup: {
      styleOverrides: {
        avatar: {
          background: alpha(pageTheme.colors.alpha.trueWhite[100], 0.1),
          color: pageTheme.colors.alpha.trueWhite[70]
        }
      }
    }
  })
});
